// ─── Talking to the server ───────────────────────────────────────────────────
//
// Small, because this product is small: what the app needs before it renders,
// who is signed in, and the calendar.
//
// The transport is `@nulife/app-ui`'s. What is here is this product's routes
// and shapes, and the one thing that cannot be shared — the timezone
// conversion. Instants go over the wire; WALL CLOCKS come back out, because
// the components render a Date through the viewer's local getters, so a Date
// handed to them must already read as the organisation's clock.
//
// The zone is a REQUIRED PARAMETER rather than module state set at startup.
// Both would work; only one is checked. A forgotten argument is a compile
// error, whereas a forgotten `configure()` call is an hour's silent drift for
// anybody whose device is set elsewhere.

import { createApiClient } from "@nulife/app-ui";
import type {
  ForumPort, ForumShape, ThreadView, ThreadDetailView, SubjectOption, AccessView,
} from "@nulife/discussion-ui";
import { toZonedWallClock, fromZonedWallClock } from "@nulife/booking-ui";
import type { CalendarPort } from "@nulife/calendar-ui";
import type { CalendarItem } from "@nulife/calendar";

const client = createApiClient();
const { request, post, send } = client;

import { ApiError } from "@nulife/app-ui";
export { ApiError };
export const whenSignedOut = client.whenSignedOut;

export type Config = {
  timeZone: string;
  organisationName: string;
  /** The tenant's palette, for `data-nl-theme`. Null means the platform
   *  default, which is what this one is on. */
  theme: string | null;
};

/** Fetched before anything else: nothing can render a time until it knows
 *  whose clock to render it on. */
export const getSettings = () => request<Config>("/settings");

export type Me = {
  signedIn: boolean;
  name?: string;
  email?: string | null;
  /** "Admin" or "Member". Absent when nobody is signed in. */
  role?: "Admin" | "Member";
  /** Whether this account may change the committee's setup. Used to stop
   *  OFFERING what cannot be done — the server refuses regardless. */
  canConfigure?: boolean;
  organisationName?: string | null;
  /** Who they are, for anything kept per person on this device — see
   *  `draftKeyFor`. Absent when nobody is signed in. */
  personId?: string;
  organisationId?: string;
  /** An Admin or the Secretary: may open hall bookings. The server checks on
   *  every bookings route regardless. */
  canManageBookings?: boolean;
};

/** Who is signed in, if anyone. Never throws for "nobody" — that is an answer,
 *  not a failure, and the app asks it on every load. */
export const getMe = () => request<Me>("/auth/me");

export const signIn = (email: string, password: string) =>
  post<{ name: string; email: string | null }>("/auth/sign-in", { email, password });

export const signOut = () => post<{ ok: true }>("/auth/sign-out", {});

// ─── Access links ────────────────────────────────────────────────────────────

/**
 * Whose link this is — a READ that must never spend it.
 *
 * Separate from accepting for a reason with an incident behind it: anything
 * that merely opens the URL (a messaging app's link preview, a safety
 * scanner) used to burn the invitation in Farndale. See `src/invites.ts`.
 */
export const previewInvite = (token: string) =>
  request<{ name: string; organisationName: string | null }>(
    `/auth/invite-preview?token=${encodeURIComponent(token)}`);

/** Spend the link and get a session. Only ever from a deliberate press. */
export const acceptInvite = (token: string) =>
  post<{ name: string }>("/auth/accept-invite", { token });

// ─── The calendar ────────────────────────────────────────────────────────────

type WireItem = Omit<CalendarItem, "startsAt" | "endsAt" | "inner"> & {
  startsAt: string; endsAt: string;
  inner?: { startsAt: string; endsAt: string } | null;
};

/**
 * Everything on between two moments.
 *
 * `failures` comes back rather than throwing. A calendar that renders empty
 * because a source failed is claiming nothing is on, and the screen has to be
 * able to say which part is missing instead.
 */
async function getCalendar(from: Date, to: Date, timeZone: string) {
  const q = new URLSearchParams({
    from: fromZonedWallClock(from, timeZone).toISOString(),
    to: fromZonedWallClock(to, timeZone).toISOString(),
  });
  const raw = await request<{
    items: WireItem[];
    failures: { source: string; error: string }[];
  }>(`/business/calendar?${q}`);
  return {
    items: raw.items.map(i => ({
      ...i,
      startsAt: toZonedWallClock(new Date(i.startsAt), timeZone),
      endsAt: toZonedWallClock(new Date(i.endsAt), timeZone),
      // The event inside a hire, on the same clock as the hire itself.
      inner: i.inner ? {
        startsAt: toZonedWallClock(new Date(i.inner.startsAt), timeZone),
        endsAt: toZonedWallClock(new Date(i.inner.endsAt), timeZone),
      } : null,
    })),
    failures: raw.failures,
  };
}

/**
 * The calendar screen's transport, bound to the committee's clock.
 *
 * A factory rather than a constant, for the zone reason above. The result MUST
 * be memoised by the caller: `CalendarSurface` has it in its fetch
 * dependencies deliberately, so a fresh object each render is a fetch each
 * render.
 *
 * The occurrence ids carry an `@` and the colons of an ISO instant, so they
 * are encoded — unencoded they would be read as a different path.
 */
export const calendarPort = (timeZone: string): CalendarPort => ({
  list: (from, to) => getCalendar(from, to, timeZone),

  addEntry: entry => post<{ id: string }>("/business/calendar/entries", {
    title: entry.title,
    startsAt: fromZonedWallClock(entry.startsAt, timeZone).toISOString(),
    endsAt: fromZonedWallClock(entry.endsAt, timeZone).toISOString(),
    allDay: entry.allDay === true,
    location: entry.location,
    description: entry.description,
    recurrence: entry.recurrence,
  }),

  skipOccurrence: id => post<{ changed: boolean }>(
    `/business/calendar/occurrences/${encodeURIComponent(id)}/skip`, {}),

  restoreOccurrence: id => post<{ changed: boolean }>(
    `/business/calendar/occurrences/${encodeURIComponent(id)}/restore`, {}),

  /** ONE occurrence, leaving the series alone. Deliberately a different call
   *  from `moveEntry`, not a flag on it: the one that could move a year of
   *  meetings should never be reachable by getting an argument wrong. */
  moveOccurrence: (id, startsAt, endsAt) => post<{ changed: boolean }>(
    `/business/calendar/occurrences/${encodeURIComponent(id)}/move`, {
      startsAt: fromZonedWallClock(startsAt, timeZone).toISOString(),
      endsAt: fromZonedWallClock(endsAt, timeZone).toISOString(),
    }),

  resetOccurrence: id => post<{ changed: boolean }>(
    `/business/calendar/occurrences/${encodeURIComponent(id)}/reset`, {}),

  moveEntry: (id, startsAt, endsAt) =>
    request<{ id: string }>(`/business/calendar/entries/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        startsAt: fromZonedWallClock(startsAt, timeZone).toISOString(),
        endsAt: fromZonedWallClock(endsAt, timeZone).toISOString(),
      }),
    }),
});

// ─── Committee Decisions ─────────────────────────────────────────────────────
//
// Issue #11. No dates cross this boundary as anything but ISO strings, and
// unlike the calendar they are shown as plain moments rather than placed on a
// grid — so they are parsed here and rendered with the viewer's own locale,
// which is what somebody reading "who said what, when" expects.

export type Area = { id: string; name: string; sortOrder: number };

export type ProposalStatus = "open" | "decided" | "withdrawn";
export type VoteChoice = "approve" | "object" | "discuss";
export type Outcome = "approved" | "not_approved" | "withdrawn";

export type ProposalSummary = {
  id: string;
  title: string;
  status: ProposalStatus;
  categoryId: string | null;
  categoryName: string | null;
  proposerName: string | null;
  createdAt: Date;
  updatedAt: Date;
  outcome: Outcome | null;
};

export type Tally = {
  approve: number; object: number; discuss: number;
  eligible: number; responded: number;
};

export type ProposalDetail = {
  id: string;
  title: string;
  description: string | null;
  status: ProposalStatus;
  categoryId: string | null;
  categoryName: string | null;
  proposerName: string | null;
  /** The discussion it was raised from, if any and if you can see it. */
  raisedIn?: { threadId: string; title: string } | null;
  createdAt: Date;
  updatedAt: Date;
  /** Whether this reader raised it. A courtesy so the screen stops offering
   *  what the server would refuse — never the permission check. */
  mine: boolean;
  comments: {
    id: string; body: string; authorName: string | null;
    mine: boolean; createdAt: Date;
  }[];
  files: {
    id: string; filename: string; mimeType: string; url: string;
    thumbnailUrl: string | null; size: number; label: string | null;
    uploadedByName: string | null; createdAt: Date;
  }[];
  /** Who this matter concerns, beyond the committee at large. Optional, and an
   *  empty list is the normal case. */
  subjects: {
    id: string; kind: "person" | "group"; subjectId: string; name: string;
    addedByName: string | null;
    /** They put themselves forward rather than being named by somebody else. */
    selfNominated: boolean;
    /** Whether to OFFER the − control. The server checks regardless. */
    canRemove: boolean;
  }[];
  /** The free-form list under the matter. Anybody may add one; only whoever
   *  wrote one may take it off. */
  notes: {
    id: string; body: string; authorName: string | null;
    mine: boolean; createdAt: Date;
  }[];
  vote: {
    tally: Tally;
    cast: { personId: string; name: string | null; choice: VoteChoice; at: Date }[];
    awaiting: { personId: string; name: string | null }[];
    mine: VoteChoice | null;
  };
  decision: {
    outcome: Outcome;
    approveCount: number; objectCount: number;
    discussCount: number; eligibleCount: number;
    decidedByName: string | null;
    note: string | null;
    decidedAt: Date;
  } | null;
};

export type DecisionRecord = {
  proposalId: string;
  title: string;
  categoryName: string | null;
  proposerName: string | null;
  outcome: Outcome;
  approveCount: number; objectCount: number;
  discussCount: number; eligibleCount: number;
  decidedByName: string | null;
  note: string | null;
  decidedAt: Date;
};

const asDate = (s: string) => new Date(s);

export const getAreas = () => request<Area[]>("/business/decisions/categories");

// ─── Who a matter concerns, and what it needs ────────────────────────────────

/**
 * Everybody and every group that can be tagged onto a matter — or, since
 * 2026-09-28, onto a task. One type, because it is one list from one pair of
 * tables and both screens render it through the same picker.
 *
 * `group` is the heading it files under in that picker ("People", "Groups"),
 * not a group in the membership sense. Optional only because Decisions'
 * endpoint predates the heading.
 */
export type Taggable = {
  kind: "person" | "group"; id: string; name: string; group?: string;
};
export const getTaggable = () => request<Taggable[]>("/business/decisions/taggable");

/** Tag a member or a group — including yourself. Answers `{added:false}` when
 *  they were already on it, which is a no-op rather than a failure. */
export const tagSubject = (proposalId: string, kind: "person" | "group", subjectId: string) =>
  post<{ added: boolean }>(
    `/business/decisions/proposals/${encodeURIComponent(proposalId)}/subjects`,
    { kind, subjectId });

export const untagSubject = (subjectRowId: string) =>
  send(`/business/decisions/subjects/${encodeURIComponent(subjectRowId)}`, { method: "DELETE" });

export const addProposalNote = (proposalId: string, body: string) =>
  post<{ id: string }>(
    `/business/decisions/proposals/${encodeURIComponent(proposalId)}/notes`, { body });

export const removeProposalNote = (noteId: string) =>
  send(`/business/decisions/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });

/**
 * Add an area, from wherever somebody notices one is missing.
 *
 * The duplicate check is the module's, not the form's: `createCategory`
 * matches on a case-insensitive `nameKey` and says which existing area clashed
 * — including when it is archived, where the answer is to restore it rather
 * than invent "Funding & Grants (2)". Surfacing the server's own words is what
 * makes that useful, so callers show the error rather than replacing it.
 */
export const addArea = (name: string) =>
  post<{ id: string; name: string }>("/business/decisions/categories", { name });

export async function getProposals(filter: {
  status?: ProposalStatus; categoryId?: string;
} = {}): Promise<ProposalSummary[]> {
  const q = new URLSearchParams();
  if (filter.status) q.set("status", filter.status);
  if (filter.categoryId) q.set("categoryId", filter.categoryId);
  const raw = await request<(Omit<ProposalSummary, "createdAt" | "updatedAt">
    & { createdAt: string; updatedAt: string })[]>(
      `/business/decisions/proposals${q.toString() ? `?${q}` : ""}`);
  return raw.map(p => ({ ...p, createdAt: asDate(p.createdAt), updatedAt: asDate(p.updatedAt) }));
}

/**
 * Everything one proposal is, in one request.
 *
 * The wire shape is spelled out rather than derived from `ProposalDetail` with
 * an intersection: `Date & string` is `never`, so the "same but with strings"
 * trick silently makes every date field unusable and the error lands three
 * lines later on the conversion itself.
 */
type WireProposal = Omit<ProposalDetail, "createdAt" | "updatedAt" | "comments" | "files" | "notes" | "vote" | "decision"> & {
  createdAt: string;
  updatedAt: string;
  comments: (Omit<ProposalDetail["comments"][number], "createdAt"> & { createdAt: string })[];
  notes: (Omit<ProposalDetail["notes"][number], "createdAt"> & { createdAt: string })[];
  files: (Omit<ProposalDetail["files"][number], "createdAt"> & { createdAt: string })[];
  vote: Omit<ProposalDetail["vote"], "cast"> & {
    cast: (Omit<ProposalDetail["vote"]["cast"][number], "at"> & { at: string })[];
  };
  decision:
    | (Omit<NonNullable<ProposalDetail["decision"]>, "decidedAt"> & { decidedAt: string })
    | null;
};

export async function getProposal(id: string): Promise<ProposalDetail> {
  const p = await request<WireProposal>(
    `/business/decisions/proposals/${encodeURIComponent(id)}`);
  return {
    ...p,
    createdAt: asDate(p.createdAt),
    updatedAt: asDate(p.updatedAt),
    comments: p.comments.map(c => ({ ...c, createdAt: asDate(c.createdAt) })),
    files: p.files.map(f => ({ ...f, createdAt: asDate(f.createdAt) })),
    notes: p.notes.map(n => ({ ...n, createdAt: asDate(n.createdAt) })),
    vote: { ...p.vote, cast: p.vote.cast.map(v => ({ ...v, at: asDate(v.at) })) },
    decision: p.decision ? { ...p.decision, decidedAt: asDate(p.decision.decidedAt) } : null,
  };
}

/** `clientActionId` is generated once per tap, so a retry after a dropped
 *  connection returns the original proposal instead of raising a second. */
/**
 * Raise a matter, with the people it concerns already on it.
 *
 * `tags` go in the SAME request (Drea, 2026-09-28) — the matter and who it
 * is for are one action, not a save followed by a second screen. `tagged`
 * comes back counted from what the server actually wrote, so a name that was
 * somehow already on it is not reported twice.
 */
export const raiseProposal = (draft: {
  title: string; categoryId: string; description?: string;
  tags?: { kind: "person" | "group"; subjectId: string }[];
}) => post<{ id: string; title: string; tagged: number }>("/business/decisions/proposals",
  { ...draft, clientActionId: crypto.randomUUID() });

export const editProposal = (id: string, patch: {
  title?: string; description?: string | null; categoryId?: string;
}) => request<{ id: string; title: string }>(
  `/business/decisions/proposals/${encodeURIComponent(id)}`,
  { method: "PATCH", body: JSON.stringify(patch) });

export const withdrawProposal = (id: string, reason?: string) =>
  post<{ outcome: Outcome }>(
    `/business/decisions/proposals/${encodeURIComponent(id)}/withdraw`, { reason });

export const addComment = (id: string, body: string) =>
  post<{ id: string }>(`/business/decisions/proposals/${encodeURIComponent(id)}/comments`,
    { body, clientActionId: crypto.randomUUID() });

export const removeComment = (commentId: string) =>
  request<{ changed: boolean }>(
    `/business/decisions/comments/${encodeURIComponent(commentId)}`, { method: "DELETE" });

export const castVote = (id: string, choice: VoteChoice) =>
  post<{ choice: VoteChoice; tally: Tally }>(
    `/business/decisions/proposals/${encodeURIComponent(id)}/vote`, { choice });

/** Closes a vote. There is deliberately no outcome to send — the result is
 *  computed from the votes, server-side, and no route accepts one. */
export const closeProposal = (id: string, note?: string) =>
  post<{ outcome: Outcome; approveCount: number; objectCount: number;
         discussCount: number; eligibleCount: number }>(
    `/business/decisions/proposals/${encodeURIComponent(id)}/close`, { note });

export async function getDecisionHistory(): Promise<DecisionRecord[]> {
  const raw = await request<(Omit<DecisionRecord, "decidedAt"> & { decidedAt: string })[]>(
    "/business/decisions/history");
  return raw.map(r => ({ ...r, decidedAt: asDate(r.decidedAt) }));
}

/**
 * Attach files to a proposal.
 *
 * Through `send()` rather than `request()`: a multipart body must not carry a
 * JSON content-type, which `request()` always sets whenever a body is present.
 * `send()` still announces a 401.
 */
export async function attachFiles(
  id: string, files: File[], label?: string,
): Promise<{ id: string; filename: string; url: string }[]> {
  const form = new FormData();
  for (const f of files) form.append("items", f, f.name);
  if (label) form.append("label", label);
  form.append("clientActionId", crypto.randomUUID());
  const res = await send(
    `/business/decisions/proposals/${encodeURIComponent(id)}/files`,
    { method: "POST", body: form },
  );
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, (body as { error?: string })?.error ?? "Could not attach that.");
  }
  return (body as { files: { id: string; filename: string; url: string }[] }).files;
}

export const removeFile = (fileId: string) =>
  request<{ changed: boolean }>(
    `/business/decisions/files/${encodeURIComponent(fileId)}`, { method: "DELETE" });

// ─── Alerts ──────────────────────────────────────────────────────────────────
//
// `@nulife/alarms`' store. One count behind every badge in this app, and the
// same one Documents will read when it lands — which is the whole point of the
// store being shared rather than each module keeping its own.

export type AlertItem = {
  id: string;
  resourceKind: string;
  resourceId: string;
  kind: string;
  title: string;
  body: string | null;
  /** Whether looking at it is enough, or a button has to be pressed. */
  needsAck: boolean;
  acknowledged: boolean;
  createdAt: Date;
};

export async function getAlerts(): Promise<{ waiting: number; alerts: AlertItem[] }> {
  const raw = await request<{
    waiting: number;
    alerts: (Omit<AlertItem, "createdAt"> & { createdAt: string })[];
  }>("/business/alerts");
  return {
    waiting: raw.waiting,
    alerts: raw.alerts.map(a => ({ ...a, createdAt: new Date(a.createdAt) })),
  };
}

/**
 * Say, explicitly, that this has been dealt with.
 *
 * Its own call, never folded into reading: "simply opening/viewing it does not
 * count as acknowledgement" — Drea, 2026-09-16. There is deliberately no
 * "acknowledge everything" here, because pressing one button to agree to an
 * unknown number of things is the opposite of an explicit action.
 */
export const acknowledgeAlert = (alertId: string) =>
  post<{ acknowledged: number }>("/business/alerts/acknowledge", { alertIds: [alertId] });

/** Mark them read. Always the caller's own — the server scopes it, and there
 *  is deliberately no parameter that could point at anybody else. */
export const markAlertsSeen = (alertIds?: string[]) =>
  post<{ cleared: number }>("/business/alerts/seen", alertIds ? { alertIds } : {});

// ─── The document library ────────────────────────────────────────────────────
//
// `DocumentsSurface` imports no API module; it is handed these. The shapes are
// the package's own, so nothing here re-describes a document — only how to
// reach one.

import type { DocumentsPort, DocumentView, LinkTarget } from "@nulife/documents-ui";

type WireDocument = Omit<DocumentView, "expiresAt" | "createdAt" | "updatedAt"> & {
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
};

const asDoc = (d: WireDocument): DocumentView => ({
  ...d,
  expiresAt: d.expiresAt ? new Date(d.expiresAt) : null,
  createdAt: new Date(d.createdAt),
  updatedAt: new Date(d.updatedAt),
});

/**
 * The library's transport.
 *
 * A factory so the caller can memoise it — the surface has it in its fetch
 * dependencies deliberately, and a fresh object each render is a fetch each
 * render.
 */
/** Library documents out of date or due soon, counted by the server now. */
export const getDocumentsNeedingAction = () =>
  request<{ count: number; expired: number; dueSoon: number }>("/business/documents/needs-action");

export const documentsPort = (
  opts: { canChangeCategories?: boolean } = {},
): DocumentsPort => ({
  categories: () => request("/business/documents/categories"),

  /** What the viewer should render, decided server-side — a Word document is
   *  only viewable once it has been converted, and the first call is what
   *  pays for that. */
  preview: id => request(`/business/documents/${id}/preview`),

  /** What is already in use, so the tag field can offer it rather than
   *  inviting somebody to retype it slightly differently. */
  tags: () => request("/business/documents/tags"),

  /** Public notes on a document (2026-10-03). The server answers only for a
   *  document this person can open. */
  notes: {
    list: id => request(`/business/documents/${encodeURIComponent(id)}/notes`),
    add: (id, body) => post(`/business/documents/${encodeURIComponent(id)}/notes`, { body }),
    remove: noteId => request(`/business/documents/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" }),
  },

  search: async filters => {
    const q = new URLSearchParams();
    if (filters.text) q.set("text", filters.text);
    // `null` means "not filed", which is a real filter and not an absence of
    // one — so it travels as the sentinel rather than being dropped.
    if (filters.categoryId === null) q.set("categoryId", "__unfiled__");
    else if (filters.categoryId) q.set("categoryId", filters.categoryId);
    if (filters.tag) q.set("tag", filters.tag);
    if (filters.mimePrefix) q.set("mimePrefix", filters.mimePrefix);
    if (filters.expiry) q.set("expiry", filters.expiry);
    const raw = await request<WireDocument[]>(
      `/business/documents${q.toString() ? `?${q}` : ""}`);
    return raw.map(asDoc);
  },

  upload: async input => {
    // Multipart, so through `send()` rather than `request()`: a multipart body
    // must not carry a JSON content-type, which `request()` always sets.
    const form = new FormData();
    form.append("file", input.file, input.file.name);
    if (input.title) form.append("title", input.title);
    if (input.description) form.append("description", input.description);
    if (input.categoryId) form.append("categoryId", input.categoryId);
    if (input.documentDate) form.append("documentDate", input.documentDate);
    if (input.expiresAt) form.append("expiresAt", input.expiresAt);
    if (input.tags?.length) form.append("tags", input.tags.join(","));
    // Always sent, even empty: the module replaces the set when the field is
    // present and leaves it alone when it is not, so omitting it would make
    // "about nothing" impossible to say.
    form.append("links", JSON.stringify(input.links ?? []));

    const res = await send("/business/documents", { method: "POST", body: form });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      throw new ApiError(res.status,
        (body as { error?: string })?.error ?? "Could not add that.");
    }
    return body as { id: string };
  },

  update: (id, patch) =>
    request(`/business/documents/${encodeURIComponent(id)}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),

  archive: id =>
    request(`/business/documents/${encodeURIComponent(id)}`, { method: "DELETE" }),

  linkables: () => request<LinkTarget[]>("/business/documents/linkables"),

  /**
   * The four together or not at all — half of this is worse than none.
   *
   * Absent entirely for a non-Admin, which is what makes the screen's courtesy
   * and the server's refusal agree: there is no control to press rather than a
   * control that fails.
   */
  categoryAdmin: opts.canChangeCategories ? {
    create: name => post("/business/documents/categories", { name }),
    rename: (id, name) =>
      request(`/business/documents/categories/${encodeURIComponent(id)}`, {
        method: "PATCH", body: JSON.stringify({ name }),
      }),
    archive: id =>
      request(`/business/documents/categories/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    reorder: ids =>
      request("/business/documents/categories/order", {
        method: "PUT", body: JSON.stringify({ ids }),
      }),
  } : undefined,
});

// ─── Compliance ──────────────────────────────────────────────────────────────

import type {
  CompliancePort, RequirementView, SectionView, ComplianceSummaryView, PersonOption,
} from "@nulife/compliance-ui";

type WireRequirement = Omit<RequirementView, "updatedAt"> & { updatedAt: string };

/**
 * The dashboard's transport.
 *
 * A factory so the caller can memoise it — the surface has it in its fetch
 * dependencies, and a fresh object each render is a fetch each render.
 */
export const compliancePort = (): CompliancePort => ({
  sections: () => request<SectionView[]>("/business/compliance/sections"),

  summary: () => request<ComplianceSummaryView>("/business/compliance/summary"),

  /** Who could be made responsible. From `@nulife/people`, which is also what
   *  the document library's linkables endpoint reads. */
  people: async () => {
    const raw = await request<{ entityKind: string; entityId: string; name: string }[]>(
      "/business/documents/linkables");
    return raw
      .filter(t => t.entityKind === "person")
      .map<PersonOption>(p => ({ id: p.entityId, name: p.name }));
  },

  requirements: async filter => {
    const q = new URLSearchParams();
    if (filter.sectionId === null) q.set("sectionId", "__unsectioned__");
    else if (filter.sectionId) q.set("sectionId", filter.sectionId);
    const raw = await request<WireRequirement[]>(
      `/business/compliance/requirements${q.toString() ? `?${q}` : ""}`);
    return raw.map(r => ({ ...r, updatedAt: new Date(r.updatedAt) }));
  },

  add: input => post("/business/compliance/requirements", input),

  update: (id, patch) =>
    request(`/business/compliance/requirements/${encodeURIComponent(id)}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),

  markCompleted: (id, on) =>
    post(`/business/compliance/requirements/${encodeURIComponent(id)}/completed`, on),

  archive: id =>
    request(`/business/compliance/requirements/${encodeURIComponent(id)}`,
      { method: "DELETE" }),
});

// ─── Ask Mira ────────────────────────────────────────────────────────────────
//
// Two calls: is there anything behind the bar, and one question.
//
// The status call exists so the home screen can say the truth without
// guessing. Until today the bar said "not connected" because nothing was;
// saying it because a key is missing, or dropping the claim once one is
// present, has to come from the server rather than from a constant in here.

export type AssistantStatus = {
  available: boolean;
  /** Why not, in words, when `available` is false. */
  reason: string | null;
  model: string;
};

export type AssistantAnswer = {
  answer: string;
  /** What was actually looked at, and how many rows came back. Shown. */
  sources: { looked: string; found: number }[];
  /** False when the reply rests on no lookup at all. The panel says so. */
  grounded: boolean;
  truncated: boolean;
  usage: { inputTokens: number; outputTokens: number; calls: number };
};

export const getAssistantStatus = () =>
  request<AssistantStatus>("/business/assistant");

export const askAssistant = (question: string) =>
  post<AssistantAnswer>("/business/assistant/ask", { question });

// ─── Members ─────────────────────────────────────────────────────────────────

export type Member = {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  /**
   * THREE different things, kept apart at the type so they cannot be confused
   * in code either — not just on screen:
   *   `accountAccess`  what they may DO in the software (Admin / Member), or
   *                    null when they have no login at all.
   *   `committeeRole`  what they ARE on the committee (Chair, Secretary,
   *                    Treasurer) — `@nulife/people`'s roles table.
   *   `tier`           what KIND of member (Committee / Volunteer / Staff /
   *                    Guest) — Memorial Hall's own.
   * Independent: somebody can be Committee tier, hold the Chair role, and
   * have no sign-in account.
   */
  accountAccess: string | null;
  committeeRole: { id: string; name: string } | null;
  /** `id` as well as `name`: `GroupTags` links to each group. */
  groups: { id: string; name: string }[];
  /** Their photograph, or null for the placeholder. The square, EXIF-corrected
   *  thumbnail the server made — not whatever came off the phone. */
  photoUrl: string | null;
  /** Committee / Volunteer / Staff / Guest. Null for anybody recorded before
   *  the tiers existed. Stored and shown; nothing enforces on it. */
  tier: MemberTier | null;
  isYou: boolean;
  /** "none" no account · "invited" an unused access link is outstanding ·
   *  "active" they have signed in. Independent of `accountAccess`: an Admin
   *  can be sitting on a link they have not used. */
  signIn: "none" | "invited" | "active";
  /** Whether to OFFER the access-link action on this row. The server's
   *  answer, not worked out here — and the route checks it again. */
  mayIssueAccessLink: boolean;
};

export const MEMBER_TIERS = ["committee", "volunteer", "staff", "guest"] as const;
export type MemberTier = (typeof MEMBER_TIERS)[number];

/** What each tier is called on screen, and the one line that says what it
 *  means. The words are the product's, not the database's. */
export const TIER_LABELS: Record<MemberTier, { label: string; hint: string }> = {
  committee: { label: "COMMITTEE", hint: "Runs the hall — votes on matters" },
  volunteer: { label: "VOLUNTEER", hint: "Helps out, not on the committee" },
  staff:     { label: "STAFF",     hint: "Paid, or contracted to the hall" },
  guest:     { label: "GUEST",     hint: "A hirer or visitor on the books" },
};

export const getMembers = () => request<Member[]>("/business/people");

/**
 * Account access — what somebody may DO in the software, as opposed to their
 * committee role or their tier. The three-way split `MemberView` describes.
 *
 * `mayChange` is the server's answer to "are you a Super Admin", asked once
 * rather than inferred from anything the client holds. The routes check it
 * again; this only decides whether to render the control.
 */
export const getAccessLevels = () =>
  request<{ levels: string[]; mayChange: boolean }>("/business/people/access-levels");

export const setAccountAccess = (personId: string, access: string) =>
  post<{ access: string }>(
    `/business/people/${encodeURIComponent(personId)}/access`, { access });

/**
 * A single-use sign-in link for one person — the button behind
 * `pnpm access-link -- "Full Name"`.
 *
 * `reissue` is the `--reissue` flag, and has to be asked for the same way:
 * a new link stops the previous one working, so pressing it on somebody who
 * already has one answers 409 (`needsReissue`) rather than quietly replacing
 * it. The caller shows that sentence and asks again.
 *
 * The returned url IS a credential. It is handed back once, to the admin who
 * pressed the button, and is never carried on the members list — see the
 * `pending` comment in `invites.ts` for why a list endpoint must not hand
 * out live tokens.
 */
export const issueAccessLink = (personId: string, reissue = false) =>
  post<{ url: string; fullName: string; reissued: boolean }>(
    `/business/people/${encodeURIComponent(personId)}/access-link`, { reissue });

// ─── Your own notification settings ──────────────────────────────────────────

export type NotificationCategory = {
  id: string; label: string; note: string;
  available: boolean; enabled: boolean;
};

export const getNotificationPreferences = () =>
  request<{ categories: NotificationCategory[]; unavailableReason: string | null }>(
    "/business/notifications/preferences");

export const setNotificationPreference = (category: string, enabled: boolean) =>
  post<{ category: string; enabled: boolean }>(
    "/business/notifications/preferences", { category, enabled });

/**
 * Add somebody to the committee's records, and change their details.
 *
 * Farndale's `savePerson`, reused rather than reinvented (Drea, 2026-09-24).
 * It creates a PERSON — a name and contact details the committee can attach
 * things to — and deliberately not a sign-in account: accounts come from their
 * own flow in Farndale too, and copying the mechanism faithfully means copying
 * that boundary.
 *
 * On edit, an omitted field is left alone and an empty string clears it. That
 * distinction is load-bearing and is Farndale's, for a bug it actually had.
 */
export type CommitteeRole = { id: string; name: string };

/** The committee's own roles — Chair, Secretary, Treasurer. Not the
 *  account-access words and not the tier. */
export const getCommitteeRoles = () =>
  request<CommitteeRole[]>("/business/people/roles");

/** Add a role the committee needs. Matched on a lowercased key server-side,
 *  so "Treasurer" and "treasurer" cannot both exist. */
export const addCommitteeRole = (name: string) =>
  post<CommitteeRole>("/business/people/roles", { name });

/** "shared": everyone who can see the member. "private": only whoever wrote
 *  it — the server never returns somebody else's. */
export type NoteVisibility = "shared" | "private";

export type MemberNote = {
  id: string; body: string; authorName: string;
  /** Whether to offer the − . Whoever wrote it may remove it. */
  mine: boolean; createdAt: string;
  visibility: NoteVisibility;
  /** "2026-10-14": the day a reminder is set for — only ever on your own
   *  private note, and read off the alarm itself. */
  remindOn: string | null;
};

export const getMemberNotes = (personId: string) =>
  request<MemberNote[]>(`/business/people/${encodeURIComponent(personId)}/notes`);

export const addMemberNote = (
  personId: string, body: string, visibility: NoteVisibility = "shared", remindOn?: string | null,
) =>
  post<{ id: string; visibility: NoteVisibility; remindOn: string | null }>(
    `/business/people/${encodeURIComponent(personId)}/notes`,
    { body, visibility, ...(remindOn ? { remindOn } : {}) });

// ─── My Profile ──────────────────────────────────────────────────────────────

/** Your own record. The first three are yours to change; the rest only an
 *  Admin can change, and are here so the screen can say so. */
export type MyProfile = {
  id: string;
  fullName: string;
  preferredName: string | null;
  email: string | null;
  phone: string | null;
  photoUrl: string | null;
  committeeRole: string | null;
  tier: MemberTier | null;
  accountAccess: string;
};

export const getMyProfile = () => request<MyProfile>("/business/people/me");

/** Contact details only. The server refuses any other field outright. */
export const updateMyProfile = (patch: { preferredName?: string; email?: string; phone?: string }) =>
  request<{ preferredName: string | null; email: string | null; phone: string | null }>(
    "/business/people/me", { method: "PATCH", body: JSON.stringify(patch) });

export const removeMemberNote = (noteId: string) =>
  send(`/business/people/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });

export const addMember = (
  draft: {
    fullName: string; email?: string; phone?: string;
    tier?: MemberTier; committeeRoleId?: string | null;
  },
) => post<{ id: string }>("/business/people", draft);

export const updateMember = (
  id: string, patch: {
    fullName?: string; email?: string; phone?: string;
    tier?: MemberTier; committeeRoleId?: string | null;
  },
) => request<{ id: string }>(`/business/people/${encodeURIComponent(id)}`, {
  method: "PATCH", body: JSON.stringify(patch),
});

/**
 * Set a member's photograph.
 *
 * Multipart, like every other upload this app makes, so it goes through the
 * same size guard and the same EXIF-aware pipeline. Answers with the url that
 * was actually stored, which is the thumbnail — the caller must not assume it
 * knows what the server kept.
 */
export const setMemberPhoto = async (personId: string, file: File) => {
  const form = new FormData();
  form.append("photo", file);
  // `send`, not a bare `fetch`: it is the shared transport, so a session that
  // has gone still reaches `whenSignedOut` and the app shows the sign-in form
  // instead of "that photo did not save". `request` cannot be used because it
  // sets a JSON content type, and a multipart body must be left to set its
  // own boundary.
  const res = await send(
    `/business/people/${encodeURIComponent(personId)}/photo`,
    { method: "POST", body: form });
  const body = await res.json().catch(() => null) as { error?: string; photoUrl?: string } | null;
  if (!res.ok) throw new ApiError(res.status, body?.error ?? "That photo did not save.");
  return { photoUrl: body!.photoUrl! };
};

export const removeMemberPhoto = (personId: string) =>
  request<{ photoUrl: null }>(
    `/business/people/${encodeURIComponent(personId)}/photo`, { method: "DELETE" });

// ─── Groups ──────────────────────────────────────────────────────────────────
//
// The tables and the domain functions have been here since the first
// migration; until tonight there was no route and no control. See
// `peopleApi.ts`'s Groups section.

export type GroupView = {
  id: string;
  name: string;
  /** How many people it actually reaches, counted from the membership rows.
   *  Zero is a real and important answer: an empty group cannot be messaged. */
  reaches: number;
  memberIds: string[];
};

export const getGroups = () => request<GroupView[]>("/business/people/groups");

/** Name it and fill it in one act — see the route's own comment for why the
 *  members are not a second call. */
export const createGroup = (name: string, memberIds: string[] = []) =>
  post<{ id: string; name: string; reaches: number }>(
    "/business/people/groups", { name, memberIds });

/** In or out. Answers with the reach the table holds afterwards, so a screen
 *  never has to guess what a toggle did. */
export const setGroupMember = (groupId: string, personId: string, member: boolean) =>
  post<{ reaches: number; memberIds: string[] }>(
    `/business/people/groups/${encodeURIComponent(groupId)}/members`,
    { personId, member });

// ─── Messages ────────────────────────────────────────────────────────────────

export type InboxThread = {
  threadId: string;
  /** A person, or a group. One inbox holds both. */
  kind: "direct" | "group";
  withName: string;
  /** Null on a group thread — there is no single other person. */
  withPersonId: string | null;
  /** Groups only: how many people the conversation reaches. */
  reaches: number | null;
  lastMessageAt: string | null;
  preview: string | null;
  unread: number;
};

export type ThreadMessage = {
  id: string;
  body: string;
  authorPersonId: string | null;
  authorName: string;
  mine: boolean;
  sentAt: string;
  editedAt: string | null;
  deleted: boolean;
  /**
   * How far your own message has got. Null on somebody else's.
   *
   * "sent" means the server has it — NOT that it reached their phone; there
   * is no device acknowledgement anywhere in this stack. "read" is real: it
   * comes from the other person's `lastReadAt`, the same marker the unread
   * badge counts from.
   */
  receipt: "sent" | "read" | null;
  /** Anything sent with it. `thumbnailUrl` is null for a file with no
   *  preview. */
  files: {
    id: string; filename: string; mimeType: string;
    url: string; thumbnailUrl: string | null; size: number;
  }[];
};

export type Conversation = {
  threadId: string;
  kind: "direct" | "group";
  withPersonId: string | null;
  withName: string | null;
  /** Groups only: how many people are in the thread. */
  reaches: number | null;
  messages: ThreadMessage[];
};

export const getInbox = () => request<InboxThread[]>("/business/messages");

/** Just the count, for the home tile. */
/**
 * How many open matters are still waiting on this person's vote.
 *
 * The pulse strip's "Yours" for PROPOSALS. A real personal scope — the
 * module's own `awaiting` definition, eligible and has not voted — rather than
 * the open total shown twice.
 */
export const getMyDecisionLoad = () =>
  request<{ awaitingMyVote: number }>("/business/decisions/awaiting-me");

export const getUnread = () =>
  request<{ unread: number; direct: number; threads: number }>("/business/messages/unread");

export const getConversation = (threadId: string) =>
  request<Conversation>(`/business/messages/thread/${encodeURIComponent(threadId)}`);

export const openConversationWith = (personId: string) =>
  post<{ threadId: string }>(`/business/messages/with/${encodeURIComponent(personId)}`, {});

/** Somebody or something a new conversation can be started with. */
export type MessageTarget = {
  kind: "person" | "group";
  id: string;
  name: string;
  /** Groups only: how many people are in it right now. */
  reaches?: number;
};

export const getMessageTargets = () =>
  request<MessageTarget[]>("/business/messages/targets");

/**
 * Open, or carry on, a group's conversation.
 *
 * Answers how many people it ACTUALLY reaches — counted from the thread's
 * participants after they were added, not from the group before. The screen
 * repeats that number rather than the one it asked for.
 */
export const openGroupConversation = (groupId: string) =>
  post<{ threadId: string; reaches: number }>(
    `/business/messages/group/${encodeURIComponent(groupId)}`, {});

/**
 * Send a message with files attached.
 *
 * Multipart so the text and the files arrive together — there is no moment
 * where the message exists and its pictures do not. `body` may be empty: the
 * server then uses the filenames, rather than inventing words nobody typed.
 */
export const sendMessageWithFiles = (threadId: string, body: string, files: File[]) => {
  const form = new FormData();
  if (body) form.append("body", body);
  for (const f of files) form.append("items", f);
  return send(`/business/messages/thread/${encodeURIComponent(threadId)}/files`, {
    method: "POST", body: form,
  }).then(r => r.json() as Promise<{ id: string }>);
};

export const sendMessage = (threadId: string, body: string) =>
  post<{ id: string; sentAt: string }>(
    `/business/messages/thread/${encodeURIComponent(threadId)}`, { body });

/**
 * Rewrite one of your own messages.
 *
 * `changed: false` is a real answer, not a failure: retyping the same words
 * is not an edit, no history row was written, and the screen must not claim
 * one. Anything that is actually refused — somebody else's message, one
 * already deleted — comes back as an error with the reason.
 */
export const editMessage = (messageId: string, body: string) =>
  request<{ changed: boolean; editedAt: string | null; edits?: number }>(
    `/business/messages/message/${encodeURIComponent(messageId)}`,
    { method: "PATCH", body: JSON.stringify({ body }) });

/** Take one of your own messages back. Soft: the thread keeps a gap saying
 *  so, and the history keeps what it said. `deleted: false` means it already
 *  was. */
export const deleteMessage = (messageId: string) =>
  request<{ deleted: boolean }>(
    `/business/messages/message/${encodeURIComponent(messageId)}`,
    { method: "DELETE" });

/** What a message said before. Fetched only when somebody asks for it —
 *  most messages are never edited. */
export type MessageVersion = {
  previousBody: string;
  editedAt: string;
  editorName: string;
};

export const getMessageHistory = (messageId: string) =>
  request<MessageVersion[]>(
    `/business/messages/message/${encodeURIComponent(messageId)}/history`);

// ─── Tasks ───────────────────────────────────────────────────────────────────

export type Task = {
  id: string;
  /** The discussion it was raised from, if any and if you can see it. */
  raisedIn?: { threadId: string; title: string } | null;
  title: string;
  description: string | null;
  status: "open" | "active" | "complete";
  priority: "low" | "medium" | "high" | null;
  dueAt: string | null;
  groupId: string | null;
  groupName: string | null;
  assignees: { personId: string; name: string; isLead: boolean }[];
  startedByName: string | null;
  completedByName: string | null;
  /** Done once, then put back. Only meaningful while `open`. */
  reopened: boolean;
  /** `name` is null when this app cannot name the thing — honest, unlike a
   *  uuid on screen. */
  links: { resourceKind: string; resourceId: string; role: string | null; name: string | null }[];
  /** Who this concerns. NOT who it is assigned to — see `assignees` for that,
   *  and the Tasks screen for why the two are deliberately different. */
  tags: {
    id: string; kind: "person" | "group"; subjectId: string; name: string;
    selfNominated: boolean;
    /** When a tagged person first opened it; null for not yet (or a group). */
    seenAt?: string | null;
  }[];
  /** You are tagged and have not taken it on. Turns "Take it" into "Accept". */
  taggedYou: boolean;
  noteCount: number;
  canRemove: boolean;
};

export type TaskNote = {
  id: string; body: string; kind: string; at: string; byName: string;
  /** The note this answers, or null for one that starts a thread. */
  replyToNoteId: string | null;
  byYou: boolean;
};
export type Linkable = { resourceKind: string; resourceId: string; name: string; group: string };
/** Something already in the document library, as the Attach picker sees it. */
export type Attachable = { id: string; name: string; group: string };
export type Attachment = {
  id: string; title: string; filename: string; mimeType: string;
  url: string; thumbnailUrl: string | null; size: number; inLibrary: boolean;
};

export const getTasks = (o: { mine?: boolean; status?: string; unassigned?: boolean } = {}) => {
  const q = new URLSearchParams();
  if (o.mine) q.set("mine", "1");
  if (o.status) q.set("status", o.status);
  if (o.unassigned) q.set("unassigned", "1");
  return request<Task[]>(`/business/tasks${q.toString() ? `?${q}` : ""}`);
};
export const getTaskNotes = (id: string) =>
  request<TaskNote[]>(`/business/tasks/${encodeURIComponent(id)}/notes`);
export const getTaskLinkables = () => request<Linkable[]>("/business/tasks/linkables");
export const createTask = (draft: {
  title: string; description?: string | null;
  priority?: string | null; dueAt?: string | null;
  links?: { resourceKind: string; resourceId: string }[];
  tags?: { kind: "person" | "group"; subjectId: string }[];
}) => post<{ id: string }>("/business/tasks", draft);
export const getTaskTaggable = () => request<Taggable[]>("/business/tasks/taggable");
export const tagTask = (id: string, kind: "person" | "group", subjectId: string) =>
  post<{ id: string | null; added: boolean }>(
    `/business/tasks/${encodeURIComponent(id)}/tags`, { kind, subjectId });
export const untagTask = (tagId: string, taskId: string) =>
  send(`/business/tasks/tags/${encodeURIComponent(tagId)}?taskId=${encodeURIComponent(taskId)}`,
    { method: "DELETE" }).then(r => r.json() as Promise<{ removed: boolean }>);
export const removeTask = (id: string) =>
  post<{ changed: boolean }>(`/business/tasks/${encodeURIComponent(id)}/archive`, {});
export const takeTask = (id: string) =>
  post<{ changed: boolean }>(`/business/tasks/${encodeURIComponent(id)}/assign`, {});
export const startTask = (id: string, note?: string) =>
  post<{ started: boolean }>(`/business/tasks/${encodeURIComponent(id)}/start`, { note });
export const completeTask = (id: string) =>
  post<{ changed: boolean }>(`/business/tasks/${encodeURIComponent(id)}/complete`, {});
export const setTaskPriority = (id: string, priority: "high" | null) =>
  post<{ changed: boolean }>(`/business/tasks/${encodeURIComponent(id)}/priority`, { priority });
export const reopenTask = (id: string) =>
  post<{ changed: boolean }>(`/business/tasks/${encodeURIComponent(id)}/reopen`, {});
export const addTaskNote = (
  id: string, body: string, kind = "note", replyToNoteId: string | null = null,
) => post<{ id: string | null }>(
  `/business/tasks/${encodeURIComponent(id)}/note`, { body, kind, replyToNoteId });

// ─── Attach ──────────────────────────────────────────────────────────────────
//
// One set of calls, parameterised by the base path, because the server is one
// router mounted twice. Tasks and Proposals attach through the same three
// paths and the same code.

/**
 * What can hold attachments.
 *
 * A named type rather than the union spelled out eight times, because
 * `discussion` is the THIRD consumer of the one attach router — Drea's
 * instruction was that attach be built once and used by both Tasks and
 * Proposals, and a discussion holding a quote is that same mechanism a third
 * time, not a new one.
 */
export type AttachOn = "task" | "proposal" | "discussion";

const ATTACH_ROOTS: Record<AttachOn, string> = {
  task: "/business/tasks",
  proposal: "/business/decisions/proposals",
  discussion: "/business/discussion/threads",
};

const attachBase = (on: AttachOn, id: string) =>
  `${ATTACH_ROOTS[on]}/${encodeURIComponent(id)}`;

const attachRoot = (on: AttachOn) => ATTACH_ROOTS[on];

export const getAttachments = (on: AttachOn, id: string) =>
  request<Attachment[]>(`${attachBase(on, id)}/attachments`);

export const getAttachable = (on: AttachOn) =>
  request<Attachable[]>(`${attachRoot(on)}/attachable`);

export const getAttachCategories = (on: AttachOn) =>
  request<{ id: string; name: string }[]>(`${attachRoot(on)}/attach-categories`);

/** Path 1: something already in the library. */
export const attachExisting = (on: AttachOn, id: string, documentId: string) =>
  post<{ id: string; title: string }>(`${attachBase(on, id)}/attachments`, { documentId });

/**
 * Paths 2 and 3: a new file, filed in Documents or not.
 *
 * `categoryId` is required by the server when `addToDocuments` is set, and
 * refused rather than silently filed under nothing — see that route.
 */
export const attachUpload = (
  on: AttachOn, id: string,
  file: File, o: { addToDocuments?: boolean; categoryId?: string | null } = {},
) => {
  const form = new FormData();
  form.append("file", file);
  if (o.addToDocuments) form.append("addToDocuments", "1");
  if (o.categoryId) form.append("categoryId", o.categoryId);
  return send(`${attachBase(on, id)}/attachments/upload`, { method: "POST", body: form })
    .then(r => r.json() as Promise<{ id: string; title: string; inLibrary: boolean }>);
};

export const detachDocument = (on: AttachOn, id: string, documentId: string) =>
  send(`${attachBase(on, id)}/attachments/${encodeURIComponent(documentId)}`,
    { method: "DELETE" }).then(r => r.json() as Promise<{ removed: boolean; archived: boolean }>);

// ─── Minutes Capture ─────────────────────────────────────────────────────────

export type MarkerKind = "off_minutes" | "attention" | "urgent" | "note";

export type MeetingMarker = {
  id: string; kind: MarkerKind; note: string | null; atOffsetMs: number;
};

export type MeetingSection = {
  id: string; name: string; position: number;
  current: { id: string; durationMs: number | null; endReason: "stopped" | "auto_stop" | null } | null;
  earlierTakes: number;
};

export type Meeting = {
  id: string;
  title: string | null;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  startedByName: string | null;
  segments: number;
  capturedMs: number;
  keptMs: number;
  sections: MeetingSection[];
  limits: { warnMs: number; maxMs: number };
  markers: MeetingMarker[];
  audioRetentionDays: number | null;
  audioPurgedAt: string | null;
};

export type MeetingSummary = {
  id: string; title: string | null; status: string;
  startedAt: string; finishedAt: string | null;
  startedByName: string | null; capturedMs: number; markerCount: number;
};

export const getMeetings = () => request<MeetingSummary[]>("/business/meetings");
/** The capture currently running, if any — so closing the tab mid-meeting
 *  does not lose it. */
export const getLiveMeeting = () => request<Meeting | null>("/business/meetings/live");
export const getMeeting = (id: string) =>
  request<Meeting>(`/business/meetings/${encodeURIComponent(id)}`);
export const startMeeting = (title?: string | null, sections?: string[]) =>
  post<Meeting>("/business/meetings", { title: title ?? null, ...(sections ? { sections } : {}) });
export const addMeetingSection = (id: string, name: string) =>
  post<{ id: string; name: string; position: number }>(
    `/business/meetings/${encodeURIComponent(id)}/sections`, { name });
export const renameMeetingSection = (sectionId: string, name: string) =>
  post<{ changed: boolean }>(
    `/business/meetings/sections/${encodeURIComponent(sectionId)}/rename`, { name });
export const removeMeetingSection = (sectionId: string) =>
  post<{ changed: boolean }>(
    `/business/meetings/sections/${encodeURIComponent(sectionId)}/remove`, {});
export const renameMeeting = (id: string, title: string | null) =>
  post<{ changed: boolean }>(`/business/meetings/${encodeURIComponent(id)}/rename`, { title });
export const addMeetingMarker = (
  id: string, kind: MarkerKind, atOffsetMs: number, note?: string | null,
) => post<MeetingMarker>(`/business/meetings/${encodeURIComponent(id)}/markers`,
  { kind, atOffsetMs, note: note ?? null });
export const removeMeetingMarker = (markerId: string) =>
  post<{ changed: boolean }>(`/business/meetings/markers/${encodeURIComponent(markerId)}/remove`, {});
export const finishMeeting = (id: string) =>
  post<{ finished: boolean; meeting: Meeting }>(`/business/meetings/${encodeURIComponent(id)}/finish`, {});

/**
 * Upload one recording segment.
 *
 * `send` rather than `post`: this is multipart, and the browser has to set its
 * own boundary. `startedAtOffsetMs` is where this segment begins on the
 * captured-audio timeline — the sum of every earlier segment's duration.
 */
export const uploadMeetingSegment = (
  id: string, blob: Blob, startedAtOffsetMs: number, durationMs: number,
  section?: { sectionId: string; endReason: "stopped" | "auto_stop" },
) => {
  const form = new FormData();
  const ext = blob.type.includes("ogg") ? "ogg" : blob.type.includes("mp4") ? "m4a" : "webm";
  form.append("audio", blob, `segment.${ext}`);
  form.append("startedAtOffsetMs", String(Math.round(startedAtOffsetMs)));
  form.append("durationMs", String(Math.round(durationMs)));
  if (section) {
    form.append("sectionId", section.sectionId);
    form.append("endReason", section.endReason);
  }
  // `send` returns the raw Response — it is the only helper that will leave
  // the Content-Type alone so the browser can set its own multipart boundary.
  return send(`/business/meetings/${encodeURIComponent(id)}/segments`, {
    method: "POST", body: form,
  }).then(async res => {
    const body = await res.json().catch(() => null) as
      { id?: string; ordinal?: number; url?: string; error?: string } | null;
    if (!res.ok) throw new Error(body?.error ?? "That segment did not upload.");
    return body as { id: string; ordinal: number; url: string };
  });
};

// ─── Discussion and the Forum ────────────────────────────────────────────────
//
// The port `@nulife/discussion-ui` is driven by. The package knows nothing
// about HTTP; this is the whole of the translation, and it is small because
// the screen asks for exactly what the server already answers. Every access
// decision is the server's — nothing here filters.

const thread = (id: string) => `/business/discussion/threads/${encodeURIComponent(id)}`;

export const forumPort = (): ForumPort => ({
  forum: () => request<ForumShape>("/business/discussion/forum"),

  threadsIn: topicId => request<ThreadView[]>(
    `/business/discussion/topics/${encodeURIComponent(topicId)}/threads`),

  shared: () => request<ThreadView[]>("/business/discussion/shared"),

  search: q => request<ThreadView[]>(`/business/discussion/search?q=${encodeURIComponent(q)}`),

  thread: threadId => request<ThreadDetailView>(thread(threadId)),

  start: data => post<{ id: string }>("/business/discussion/threads", data),

  reply: (threadId, body, mentions) => post<{ id: string }>(
    `${thread(threadId)}/replies`, { body, mentions }),

  withdrawReply: (threadId, replyId) => send(
    `${thread(threadId)}/replies/${encodeURIComponent(replyId)}`,
    { method: "DELETE" },
  ).then(res => {
    if (!res.ok) throw new ApiError(res.status, "There is nothing of yours to withdraw there.");
    return res;
  }),

  editReply: (threadId, replyId, body) => send(
    `${thread(threadId)}/replies/${encodeURIComponent(replyId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body }) },
  ).then(async res => {
    const data = await res.json().catch(() => ({})) as { changed?: boolean; error?: string };
    if (!res.ok) throw new ApiError(res.status, data.error ?? "That reply was not changed.");
    return { changed: data.changed === true };
  }),

  setStatus: (threadId, status) => post<{ status: string }>(`${thread(threadId)}/status`, { status }),

  // The server reads the stored state back rather than echoing what was sent,
  // so this returns the truth even if the write did something else.
  setMuted: (threadId, muted) => post<{ muted: boolean }>(`${thread(threadId)}/mute`, { muted }),

  taggable: scope => {
    const qs = scope.threadId ? `?threadId=${encodeURIComponent(scope.threadId)}`
      : scope.topicId ? `?topicId=${encodeURIComponent(scope.topicId)}` : "";
    return request<SubjectOption[]>(`/business/discussion/taggable${qs}`);
  },

  tag: (threadId, subject) => post<{ notified: number }>(`${thread(threadId)}/tags`, subject),

  untag: (threadId, tagId) => send(
    `${thread(threadId)}/tags/${encodeURIComponent(tagId)}`,
    { method: "DELETE" },
  ),

  ask: (threadId, personIds) => post<{ asked: number }>(`${thread(threadId)}/asks`, { personIds }),

  closeAsk: askId => post<{ closed: boolean }>(
    `/business/discussion/asks/${encodeURIComponent(askId)}/close`, {}),

  seen: threadId => request<{ personId: string; name: string; lastViewedAt: string | null }[]>(
    `${thread(threadId)}/seen`),

  escalate: (threadId, spec) => post<{ kind: string; id: string }>(`${thread(threadId)}/escalate`, spec),

  decideAuthorisation: (threadId, authId, outcome) => post<{ status: string }>(
    `${thread(threadId)}/authorisations/${encodeURIComponent(authId)}/decide`, { outcome }),

  revokeAuthorisation: (threadId, authId) => post<{ status: string }>(
    `${thread(threadId)}/authorisations/${encodeURIComponent(authId)}/revoke`, {}),

  proposalCategories: () => getAreas().then(areas => areas.map(a => ({ id: a.id, name: a.name }))),

  members: () => getMembers().then(people => people
    .map(p => ({ id: p.id, name: p.fullName }))
    .sort((a, b) => a.name.localeCompare(b.name))),

  createTopic: data => post<{ id: string }>("/business/discussion/topics", data),

  updateTopic: (topicId, patch) => request<unknown>(
    `/business/discussion/topics/${encodeURIComponent(topicId)}`,
    { method: "PATCH", body: JSON.stringify(patch) }),

  access: node => request<AccessView>(
    `/business/discussion/access/${node.kind}/${encodeURIComponent(node.id)}`),

  setAccess: (node, personId, access) => post<{ hasAccess: boolean }>(
    `/business/discussion/access/${node.kind}/${encodeURIComponent(node.id)}`, { personId, access }),

  setGroupAccess: (node, groupId, access) => post<{ hasAccess: boolean }>(
    `/business/discussion/access/${node.kind}/${encodeURIComponent(node.id)}/groups`, { groupId, access }),

  setRestricted: (node, restricted) => post<{ restricted: boolean }>(
    `/business/discussion/access/${node.kind}/${encodeURIComponent(node.id)}/restricted`, { restricted }),
});


// ─── Hall bookings (Admin + Secretary, 2026-10-03) ──────────────────────────
//
// Crafting Comb's business calls, renamed for a hall: a service is a hire
// type, a client is a hirer. Wall clocks for the components, instants for the
// server, converted here and nowhere else.

export type HireType = {
  id: string; name: string; durationMinutes: number;
  description: string | null; priceMinor: number | null; currency: string | null;
};
export type HallSlot = { resourceId: string; startsAt: Date; endsAt: Date; instant: Date };
export type HallBooking = {
  id: string; startsAt: Date; endsAt: Date; instant: Date;
  status: "requested" | "confirmed" | "declined" | "cancelled" | "blocked";
  blockReason: string | null; notes: string | null;
  priceMinor: number | null; currency: string | null;
  personName: string | null; serviceName: string | null;
  /** The event inside the hire, on the hall's wall clock. Null for none:
   *  `startsAt`/`endsAt` above are the HIRE (Drea, 2026-10-04). */
  eventStartsAt: Date | null; eventEndsAt: Date | null;
  /** Which room this booking is, and which hire it is part of (2026-10-05). */
  roomName: string | null; hireId: string | null;
  /** The room's place in Setup's list, so a hire's rooms read in that order. */
  roomOrder: number | null;
};
export type HallMonthDay = { date: string; working: boolean; openSlots: number; bookings: number };
export type HoursRule = { weekday: number; startMinute: number; endMinute: number };
export type Hirer = { id: string; fullName: string; phone: string | null; email: string | null; bookings: number };

export const getHireTypes = () => request<HireType[]>("/business/bookings/services");
export const getHallHours = () => request<HoursRule[]>("/business/bookings/hours");
export const getHirers = () => request<Hirer[]>("/business/bookings/hirers");

export const getHallMonth = (serviceId: string, year: number, month: number) =>
  request<HallMonthDay[]>(
    `/business/bookings/month?${new URLSearchParams({ serviceId, year: String(year), month: String(month) })}`);

export async function getHallBookings(from: Date, to: Date, timeZone: string): Promise<HallBooking[]> {
  const q = new URLSearchParams({
    from: fromZonedWallClock(from, timeZone).toISOString(),
    to: fromZonedWallClock(to, timeZone).toISOString(),
  });
  const raw = await request<(Omit<HallBooking, "startsAt" | "endsAt" | "instant" | "eventStartsAt" | "eventEndsAt">
    & { startsAt: string; endsAt: string; eventStartsAt: string | null; eventEndsAt: string | null })[]>(
    `/business/bookings?${q}`);
  return raw.map(b => ({
    ...b,
    startsAt: toZonedWallClock(new Date(b.startsAt), timeZone),
    endsAt: toZonedWallClock(new Date(b.endsAt), timeZone),
    instant: new Date(b.startsAt),
    eventStartsAt: b.eventStartsAt ? toZonedWallClock(new Date(b.eventStartsAt), timeZone) : null,
    eventEndsAt: b.eventEndsAt ? toZonedWallClock(new Date(b.eventEndsAt), timeZone) : null,
  }));
}

export async function getHallAvailability(serviceId: string, from: Date, to: Date, timeZone: string): Promise<HallSlot[]> {
  const q = new URLSearchParams({ serviceId, from: from.toISOString(), to: to.toISOString() });
  const raw = await request<{ resourceId: string; startsAt: string; endsAt: string }[]>(
    `/business/bookings/availability?${q}`);
  return raw.map(s => ({
    resourceId: s.resourceId,
    startsAt: toZonedWallClock(new Date(s.startsAt), timeZone),
    endsAt: toZonedWallClock(new Date(s.endsAt), timeZone),
    instant: new Date(s.startsAt),
  }));
}

export const addHirer = (draft: { fullName: string; phone: string; email?: string }) =>
  post<{ id: string; fullName: string; phone: string | null; created: boolean }>("/business/bookings/hirers", draft);

/** CONFIRMED on arrival: the Secretary taking it is the approval. The slot's
 *  instant is sent, never re-derived from the wall clock. */
export const addHallBooking = (input: {
  hirerId: string; serviceId: string; slotInstant: Date; clientActionId: string; notes?: string;
  /** The hire's end, as an instant, when it is not the hire type's length. */
  endsInstant?: Date;
  /** The event inside the hire, as instants. */
  event?: { startsInstant: Date; endsInstant: Date } | null;
}) => post<{ bookingId: string; status: string; eventSaved: boolean; eventError: string | null }>("/business/bookings", {
  hirerId: input.hirerId, serviceId: input.serviceId,
  startsAt: input.slotInstant.toISOString(),
  endsAt: input.endsInstant?.toISOString(),
  eventStartsAt: input.event?.startsInstant.toISOString(),
  eventEndsAt: input.event?.endsInstant.toISOString(),
  clientActionId: input.clientActionId, notes: input.notes,
});

/** Set or clear the event inside an existing hire. Wall clocks in, as the
 *  screen holds them; instants on the wire. */
export const setHallBookingEvent = (id: string, event: { startsAt: Date; endsAt: Date } | null, timeZone: string) =>
  request<{ eventStartsAt: string | null; eventEndsAt: string | null }>(
    `/business/bookings/${encodeURIComponent(id)}/event`, {
      method: "PUT",
      body: JSON.stringify(event ? {
        eventStartsAt: fromZonedWallClock(event.startsAt, timeZone).toISOString(),
        eventEndsAt: fromZonedWallClock(event.endsAt, timeZone).toISOString(),
      } : {}),
    });

export const moveHallBooking = (id: string, startsAt: Date, timeZone: string, wasAt?: Date) =>
  post<{ id: string }>(`/business/bookings/${encodeURIComponent(id)}/move`,
    { startsAt: fromZonedWallClock(startsAt, timeZone, { near: wasAt }).toISOString() });

export const cancelHallBooking = (id: string) =>
  post<{ changed: boolean; status: string | null }>(`/business/bookings/${encodeURIComponent(id)}/status`, { status: "cancelled" });

export const blockHallTime = (startsAt: Date, endsAt: Date, timeZone: string, reason?: string) =>
  post<{ id: string }>("/business/bookings/block", {
    startsAt: fromZonedWallClock(startsAt, timeZone).toISOString(),
    endsAt: fromZonedWallClock(endsAt, timeZone).toISOString(),
    reason,
  });

export const addHireType = (draft: { name: string; durationMinutes: number; description?: string; priceMinor?: number | null }) =>
  post<{ id: string }>("/business/bookings/services", draft);

export const saveHireType = (id: string, patch: { name: string; durationMinutes: number; priceMinor: number | null }) =>
  request<{ id: string }>(`/business/bookings/services/${encodeURIComponent(id)}`, {
    method: "PATCH", body: JSON.stringify(patch),
  });

export const removeHireType = (id: string) =>
  request<{ changed: boolean }>(`/business/bookings/services/${encodeURIComponent(id)}`, { method: "DELETE" });

export const saveHallHours = (hours: { weekday: number; open: boolean; from: string; to: string }[]) =>
  request<{ ok: true }>("/business/bookings/hours", { method: "PUT", body: JSON.stringify({ hours }) });

// ─── The Minutes tile (2026-10-03) ──────────────────────────────────────────

export type MinutesDelivery = {
  name: string; email: string | null;
  /** What the mail provider said, read back — or no_email for a committee
   *  member with no address on Members. */
  status: "sent" | "failed" | "not_sent" | "no_email";
  detail: string | null;
};
export type MinutesItem = {
  id: string; title: string; url: string | null; mimeType: string | null;
  documentId: string | null; filename: string | null;
  meetingDate: string | null; uploadedAt: string; uploadedBy: string | null;
  approved: { at: string; by: string | null } | null;
  deliveries: MinutesDelivery[];
};
export type MinutesList = {
  canApprove: boolean; emailReady: boolean; committee: number; minutes: MinutesItem[];
};

export const getMinutes = () => request<MinutesList>("/business/minutes");

export async function uploadMinutes(file: File, meetingDate: string | null) {
  const form = new FormData();
  form.append("file", file);
  if (meetingDate) form.append("meetingDate", meetingDate);
  const res = await send("/business/minutes", { method: "POST", body: form });
  const body = await res.json().catch(() => ({})) as { id?: string; error?: string };
  if (!res.ok) throw new ApiError(res.status, body.error ?? "That file was not filed.");
  return body as { id: string };
}

export const approveMinutes = (id: string) =>
  post<{ approved: boolean; changed: boolean }>(`/business/minutes/${encodeURIComponent(id)}/approve`, {});

export const sendMinutesAgain = (id: string) =>
  post<{ ok: true }>(`/business/minutes/${encodeURIComponent(id)}/send`, {});

// ─── Clients (2026-10-04) ────────────────────────────────────────────────────
//
// A client is a relationship on a Person or an Organisation (Drea, via Sol).
// Admin and the Secretary only, like Bookings.

export type ClientKind = "person" | "organisation";
export type ClientSummary = {
  kind: ClientKind; id: string; name: string; phone: string | null; email: string | null;
  mainContact: string | null; bookings: number;
};
export type ClientDetail = {
  kind: ClientKind; id: string; name: string; phone: string | null; email: string | null;
  extraEmails: string[]; address: string | null; standingNote: string | null;
  contacts: { linkId: string; personId: string; role: string; name: string; phone: string | null; email: string | null }[];
  notes: { id: string; body: string; at: string }[];
  history: { bookingId: string; startsAt: string; endsAt: string; status: string; hireType: string | null;
    eventStartsAt: string | null; eventEndsAt: string | null }[];
  emails: { available: boolean; reason: string };
};
export type ClientFields = {
  name?: string; phone?: string | null; email?: string | null; extraEmails?: string[];
  address?: string | null; standingNote?: string | null;
};

const clientPath = (kind: ClientKind, id: string) => `/business/clients/${kind}/${encodeURIComponent(id)}`;

export const getClients = () => request<ClientSummary[]>("/business/clients");
export const getClient = (kind: ClientKind, id: string) => request<ClientDetail>(clientPath(kind, id));
export const addClient = (kind: ClientKind, fields: ClientFields) =>
  post<{ kind: ClientKind; id: string; created: boolean }>("/business/clients", {
    kind, ...fields, ...(kind === "person" ? { fullName: fields.name } : {}),
  });
export const saveClient = (kind: ClientKind, id: string, fields: ClientFields) =>
  request<{ changed: boolean }>(clientPath(kind, id), { method: "PATCH", body: JSON.stringify(fields) });
export const addClientContact = (orgId: string, contact: { role: string; fullName: string; phone?: string; email?: string }) =>
  post<{ linkId: string | null; personId: string; added: boolean }>(`/business/clients/organisation/${encodeURIComponent(orgId)}/contacts`, contact);
export const removeClientContact = (orgId: string, linkId: string) =>
  request<{ removed: boolean }>(`/business/clients/organisation/${encodeURIComponent(orgId)}/contacts/${encodeURIComponent(linkId)}`, { method: "DELETE" });
export const addClientNote = (kind: ClientKind, id: string, body: string) =>
  post<{ id: string }>(`${clientPath(kind, id)}/notes`, { body });
export const removeClientNote = (noteId: string) =>
  request<{ removed: boolean }>(`/business/clients/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });

// ─── Setup (2026-10-04) ──────────────────────────────────────────────────────

export type OrganisationCard = {
  name: string | null; address: string | null; phone: string | null; email: string | null;
  registeredStatus: string | null; charityNumber: string | null;
};
export type HallRoom = { id: string; name: string };
export const getOrganisationCard = () => request<OrganisationCard>("/business/setup/organisation");
export const saveOrganisationCard = (patch: Partial<Omit<OrganisationCard, "name">>) =>
  request<{ changed: boolean }>("/business/setup/organisation", { method: "PATCH", body: JSON.stringify(patch) });
export const getRooms = () => request<HallRoom[]>("/business/setup/rooms");
export const addRoom = (name: string) => post<HallRoom[]>("/business/setup/rooms", { name });
export const renameRoom = (id: string, name: string) =>
  request<{ changed: boolean }>(`/business/setup/rooms/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ name }) });
export const removeRoom = (id: string) =>
  request<{ removed: boolean }>(`/business/setup/rooms/${encodeURIComponent(id)}`, { method: "DELETE" });

// ─── Hires: rooms, rates, cost (2026-10-05) ──────────────────────────────────

export type HireQuote = {
  rate: { id: string; name: string; perHalfHourMinor: number };
  rooms: { id: string; name: string }[];
  startsAt: string; endsAt: string; halfHours: number;
  perRoomMinor: number; totalMinor: number;
};
export const getBookableRooms = () => request<{ id: string; name: string }[]>("/business/bookings/rooms");

type HireInput = {
  rateId: string; roomIds: string[]; startsAt: Date; endsAt: Date;
  event?: { startsAt: Date; endsAt: Date } | null;
};
const hireBody = (h: HireInput, timeZone: string) => ({
  rateId: h.rateId, roomIds: h.roomIds,
  startsAt: fromZonedWallClock(h.startsAt, timeZone).toISOString(),
  endsAt: fromZonedWallClock(h.endsAt, timeZone).toISOString(),
  eventStartsAt: h.event ? fromZonedWallClock(h.event.startsAt, timeZone).toISOString() : undefined,
  eventEndsAt: h.event ? fromZonedWallClock(h.event.endsAt, timeZone).toISOString() : undefined,
});
/** What a hire would cost: the server's own sum, the one it would store. */
export const quoteHire = (h: HireInput, timeZone: string) =>
  post<HireQuote>("/business/bookings/quote", hireBody(h, timeZone));
export const addHire = (h: HireInput & { hirerId: string; clientActionId: string }, timeZone: string) =>
  post<HireQuote & { hireId: string; bookingIds: string[]; eventSaved: boolean; eventError: string | null }>(
    "/business/bookings/hire", { ...hireBody(h, timeZone), hirerId: h.hirerId, clientActionId: h.clientActionId });

/** Pounds and pence, from minor units. */
export const money = (minor: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(minor / 100);

/** Room names for the calendar's "Where", and earlier calendar titles. */
export const getRoomNames = () => request<string[]>("/business/rooms");
export const getCalendarTitles = () => request<string[]>("/business/calendar/titles");

/** The signed-in person opened this task (2026-10-05: "who has seen it"). */
export const markTaskSeen = (id: string) =>
  post<{ seen: boolean }>(`/business/tasks/${encodeURIComponent(id)}/seen`, {});


// ─── Media (issue #16) ───────────────────────────────────────────────────────

import type { MediaPort, MediaItemView, MediaFolderView } from "@nulife/media-ui";

export const mediaPort = (): MediaPort => {
  const qs = (o: Record<string, string | undefined>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(o)) if (v) q.set(k, v);
    return q.toString() ? `?${q}` : "";
  };
  const bulk = (action: string, body: object) => post(`/business/media/items/${action}`, body);
  return {
    folders: async () => (await request<{ folders: MediaFolderView[] }>("/business/media/folders")).folders,
    items: f => request(`/business/media/items${qs({
      q: f.q, tag: f.tag, uploader: f.uploader, from: f.from, to: f.to, type: f.type,
      archived: f.archived ? "1" : undefined,
    })}`),
    upload: async (files, o) => {
      // Multipart, so through `send()` — see documentsPort.upload.
      const form = new FormData();
      if (o.folderId) form.append("folderId", o.folderId);
      if (o.date) form.append("date", o.date);
      if (o.tags?.length) form.append("tags", o.tags.join(","));
      for (const f of files) form.append("files", f, f.name);
      const res = await send("/business/media/items", { method: "POST", body: form });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new ApiError(res.status, (body as { error?: string })?.error ?? "Could not add that.");
      return (body as { items: MediaItemView[] }).items;
    },
    edit: async (ids, patch) => { await bulk("edit", { ids, ...patch }); },
    move: async (ids, folderId) => { await bulk("move", { ids, folderId }); },
    tag: async (ids, change) => { await bulk("tag", { ids, ...change }); },
    archive: async ids => { await bulk("archive", { ids }); },
    restore: async ids => { await bulk("restore", { ids }); },
    createFolder: async f => { await post("/business/media/folders", f); },
    updateFolder: async (id, patch) => { await request(`/business/media/folders/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) }); },
    archiveFolder: async id => { await request(`/business/media/folders/${encodeURIComponent(id)}`, { method: "DELETE" }); },
  };
};
